/* eslint-disable @typescript-eslint/no-empty-function */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  selector: 'irembogov-currency-format-input',
  templateUrl: './irembogov-currency-format-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IrembogovCurrencyFormatInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IrembogovCurrencyFormatInputComponent),
      multi: true,
    },
  ],
})
export class IrembogovCurrencyFormatInputComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy, Validator
{
  @Input() placeholder? = '';
  @Input() value: string | number = '';
  @Input() customFormControl = new FormControl();
  @Input() requiredField = false;
  @Output() valueChange = new EventEmitter<number | null>();

  formattedValue = '';
  private _onChange = (value: unknown) => {};
  public _onTouch = (value: unknown) => {};
  private _onValidationChange = () => {};
  private subscriptions = new Subscription();

  constructor(private readonly cdRef: ChangeDetectorRef) {}

  writeValue(valueStr: string): void {
    const rawValue = typeof valueStr === 'number' ? valueStr : valueStr || '';
    const cleaned = rawValue.replace(/[^0-9]/g, '');
    const numericValue = cleaned ? parseInt(cleaned, 10) : null;
    this.formattedValue = this.format(cleaned);
    this.customFormControl.setValue(numericValue, { emitEvent: false });
  }

  ngAfterViewInit(): void {
    if (this.customFormControl.value) {
      this.customFormControl.updateValueAndValidity({ emitEvent: true });
      this._onValidationChange();
    }
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidationChange = fn;
  }

  registerOnTouched(fn: (_: unknown) => void): void {
    this._onTouch = fn;
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this._onChange = fn;
  }

  validate(formControl: FormControl): ValidationErrors | null {
    const validationErrors: ValidationErrors = {};
    this.cdRef.detectChanges();

    if (this.requiredField && !formControl.value) {
      validationErrors['required'] = true;
    }

    return Object.keys(validationErrors).length ? validationErrors : null;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value.replace(/[^0-9]/g, '');
    this.formattedValue = this.format(rawValue);
    input.value = this.formattedValue;
    const numericValue = rawValue ? parseInt(rawValue, 10) : null;
    this.customFormControl.setValue(numericValue);
    this._onChange(numericValue);
    this.valueChange.emit(numericValue);
  }

  format(value: string): string {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
